import { BEIGE_600, BOX_SHADOW_SM, CORAL_600, CORAL_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Badge, Divider, Stack, Typography } from '@mui/material'
import { PendingWorkspaceInvitationItem, PendingWorkspaceInvitationItemVariant } from 'components/common/TopBar/PendingWorkspaceInvitationItem'

import AddIcon from '@mui/icons-material/Add'
import { AnalyticsEvent } from 'utils/analytics'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { NameAvatar } from 'components/common/Avatars/NameAvatar.component'
import { Path } from 'constants/router'
import { PropstackLinkedBadge } from 'components/common/PropstackLinkedBadge'
import { useNavigate } from 'react-router'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WorkspaceRole } from 'models/user'
import { getSeatTypeAccessLabelKey } from 'constants/workspaces'
import { useWorkspaceList } from 'utils/hooks'

/**
 * @interface Props for WorkspaceDropdownMobile component
 */
export interface Props {
  /** Callback to close the parent mobile menu */
  onCloseMenu?: (e: React.MouseEvent<Element, MouseEvent>) => void
  /** Enriched analytics event handler that includes userEmail and organizationId */
  handleLogAnalyticsEvent: (event: AnalyticsEvent, options?: {}) => void
}

/**
 * @component WorkspaceDropdownMobile displayed in mobile TopBar
 * @example <WorkspaceDropdownMobile />
 */
export const WorkspaceDropdownMobile: React.FC<Props> = ({ onCloseMenu, handleLogAnalyticsEvent }) => {
  const { t } = useTranslation(['workspace_profile'])
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  // Suppress close while a badge popover is open — it portals outside the ClickAwayListener (QA-303).
  const [openPopovers, setOpenPopovers] = useState(0)
  const handlePopoverToggle = useCallback((open: boolean) => setOpenPopovers((count) => (open ? count + 1 : Math.max(0, count - 1))), [])

  const {
    displayedWorkspace,
    activeWorkspaceList,
    isWorkspacePending,
    isWorkspaceResolved,
    hasPendingInvitations,
    selectWorkspace,
    getPropstackMembership
  } = useWorkspaceList()

  const handleButtonClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation()

    if (!isOpen) {
      handleLogAnalyticsEvent(AnalyticsEvent.WORKSPACE_DROPDOWN_OPENED, {})
    }

    // Toggle dropdown: if open, close it; if closed, open it
    setIsOpen((prev) => !prev)
  }

  const workspaceName = displayedWorkspace?.name ?? t(isWorkspaceResolved ? 'missing_workspace' : 'loading_workspaces')

  return (
    <MUIDropdown
      open={isOpen}
      onClose={() => { if (openPopovers === 0) setIsOpen(false) }}
      sx={{ marginTop: '-4.4rem !important' }}
      button={(dropdownIsOpen) => (
        <Badge
          overlap="rectangular"
          variant="dot"
          invisible={!hasPendingInvitations}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ display: 'flex', alignSelf: 'stretch', '& .MuiBadge-badge': { backgroundColor: CORAL_700, top: '.4rem', right: '.2rem' } }}
        >
          <Stack
            flexDirection="row"
            onClick={handleButtonClick}
            sx={{
              display: 'flex',
              flex: 1,
              padding: '.8rem .4rem',
              justifyContent: 'space-between',
              alignItems: 'center',
              alignSelf: 'stretch',
              cursor: 'pointer',
            }}
          >

            <Stack flexDirection="row" alignItems="center" gap=".8rem" overflow="hidden">

              {displayedWorkspace && <Stack flexShrink={0}>
                <NameAvatar
                  name={workspaceName}
                  size="2.4rem"
                  fontWeight={350}
                  textVariant='text-xs'
                  backgroundColor={CORAL_600}
                  borderColor="transparent"
                  textColor={WHITE}
                  elevation="none"
                />
              </Stack>}

              <Typography
                variant="text-md"
                fontWeight={550}
                color={GRAY_900}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {workspaceName}
              </Typography>

              {displayedWorkspace && isWorkspacePending(displayedWorkspace) &&
                <MUIBadge color="blue" size="sm" label={t('pending_invitation')} />
              }

            </Stack>

            {dropdownIsOpen
              ? <KeyboardArrowUpOutlinedIcon sx={{ fontSize: '1.8rem', color: GRAY_900 }} />
              : <KeyboardArrowDownOutlinedIcon sx={{ fontSize: '1.8rem', color: GRAY_900 }} />
            }

          </Stack>
        </Badge>
      )}
    >
      <Stack
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          width: 'calc(100vw - 3.2rem)',
          padding: '1.2rem',
          flexDirection: 'column',
          alignItems: 'flex-start',
          background: WHITE,
          border: `1px solid ${BEIGE_600}`,
          borderRadius: '.8rem',
          boxShadow: BOX_SHADOW_SM,
        }}
      >
        <Stack
          sx={{
            maxHeight: '23.6rem',
            overflowY: 'auto',
            gap: '1.2rem',
            width: '100%',
          }}
        >
          {activeWorkspaceList.map((workspaceItem) => {
            const isPending = isWorkspacePending(workspaceItem)

            if (isPending) {
              return (
                <PendingWorkspaceInvitationItem
                  key={workspaceItem.id}
                  workspace={workspaceItem}
                  variant={PendingWorkspaceInvitationItemVariant.MOBILE}
                />
              )
            }

            return (
              <Stack
                key={workspaceItem.id}
                gap="1.2rem"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                borderRadius=".4rem"
                onClick={(e) => {
                  if (workspaceItem.id !== displayedWorkspace?.id) {
                    handleLogAnalyticsEvent(AnalyticsEvent.WORKSPACE_SWITCHED, {
                      sourceWorkspaceId: displayedWorkspace?.id,
                      destinationWorkspaceId: workspaceItem.id,
                    })
                  }
                  selectWorkspace(workspaceItem)
                  setIsOpen(false)
                  onCloseMenu?.(e)
                  navigate(Path.INDEX)
                }}
                sx={{
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >

                <Stack
                  gap=".8rem"
                  flexDirection="row"
                  alignItems="center"
                  flex={1}
                  overflow="hidden"
                >
                  <Stack flexShrink={0}>
                    <NameAvatar
                      name={workspaceItem.name}
                      size="2.4rem"
                      fontWeight={350}
                      textVariant='text-xs'
                      backgroundColor={CORAL_600}
                      borderColor="transparent"
                      textColor={WHITE}
                      elevation="none"
                    />
                  </Stack>

                  <Typography
                    variant="text-md"
                    color={GRAY_900}
                    fontWeight={550}
                    sx={{
                      lineHeight: '3.2rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {workspaceItem.name}
                  </Typography>

                  <PropstackLinkedBadge workspace={getPropstackMembership(workspaceItem.id)} isAdmin={workspaceItem.role === WorkspaceRole.ADMIN} onPopoverToggle={handlePopoverToggle} />

                  <MUIBadge color="gray" size="sm" label={t(getSeatTypeAccessLabelKey(workspaceItem.seatType))} sx={{ flexShrink: 0 }} />
                </Stack>

                {workspaceItem.id === displayedWorkspace?.id && <KeyboardArrowUpOutlinedIcon sx={{ fontSize: '1.8rem', color: GRAY_900 }} />}
              </Stack>
            )
          })}
        </Stack>

        {displayedWorkspace && <Divider sx={{ width: 'calc(100% + 2.4rem)', marginLeft: '-1.2rem', borderColor: BEIGE_600 }} />}

        <Stack
          direction="row"
          alignItems="center"
          gap=".8rem"
          width="100%"
          paddingTop="1.2rem"
          onClick={(e) => {
            handleLogAnalyticsEvent(AnalyticsEvent.WORKSPACE_CREATION_INITIATED, { currentWorkspaceId: displayedWorkspace?.id })
            setIsOpen(false)
            onCloseMenu?.(e)
            navigate(Path.WORKSPACE_CREATION)
          }}
          sx={{ cursor: 'pointer' }}
        >
          <AddIcon sx={{ color: GRAY_900, fontSize: '2.4rem' }} />
          <Typography variant="text-md" color={GRAY_900} fontWeight={350}>{t('create_new_workspace')}</Typography>
        </Stack>
      </Stack>
    </MUIDropdown>
  )
}
